import React, { useState, useEffect, useRef } from 'react';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';

export default function AppSearchSelect({ 
  value, 
  onChange, 
  selectedAppName = null,
  placeholder = "Search apps by name or owner email..."
}) {
  const { authFetch } = useAuthContext();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedApp, setSelectedApp] = useState(selectedAppName ? { id: value, name: selectedAppName } : null);
  const containerRef = useRef(null);

  // Update selected app when value changes externally
  useEffect(() => {
    if (value && selectedAppName) {
      setSelectedApp({ id: value, name: selectedAppName });
    } else if (!value) {
      setSelectedApp(null);
    }
  }, [value, selectedAppName]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search function - called on button click or Enter key
  const performSearch = async () => {
    if (search.length < 2) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    setHasSearched(true);
    setIsOpen(true);
    try {
      const response = await authFetch(`/api/crm/search-apps?q=${encodeURIComponent(search)}`);
      if (response.ok) {
        const data = await response.json();
        setResults(data.apps || []);
      }
    } catch (error) {
      console.error('Failed to search apps:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      performSearch();
    }
  };

  const handleSelect = (app) => {
    setSelectedApp(app);
    onChange(app.id);
    setIsOpen(false);
    setSearch('');
    setResults([]);
    setHasSearched(false);
  };

  const handleClear = () => {
    setSelectedApp(null);
    onChange(null);
  };

  return (
    <div ref={containerRef} className="relative">
      {selectedApp ? (
        // Selected app display
        <div className="flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-zapt-purple">📱</span>
            <span className="truncate font-medium text-gray-900">{selectedApp.name}</span>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="ml-2 text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : (
        // Search input with button
        <div>
          <div className="flex gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
            />
            <button
              type="button"
              onClick={performSearch}
              disabled={search.length < 2 || isLoading}
              className="px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <span className="text-sm">...</span>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span className="text-sm">Search</span>
                </>
              )}
            </button>
          </div>
          
          {/* Dropdown */}
          {isOpen && hasSearched && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {isLoading ? (
                <div className="px-4 py-3 text-sm text-gray-500">Searching...</div>
              ) : results.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500">No apps found</div>
              ) : (
                results.map((app) => (
                  <button
                    key={app.id}
                    type="button"
                    onClick={() => handleSelect(app)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100 last:border-0"
                  >
                    <span className="text-zapt-purple">📱</span>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">{app.name}</p>
                      <p className="text-xs text-gray-500 truncate">{app.userEmail} • {app.status}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
